import { Link, NavLink } from "react-router-dom";
import { LayoutDashboard, FolderOpen, Calendar, Wallet, LogOut, Sun, Moon, Megaphone, Receipt as ReceiptIcon, Heart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import useDesktopNotifications from "@/hooks/useDesktopNotifications";

const portalNav = [
  { to: "/portal", label: "Overview", icon: LayoutDashboard },
  { to: "/portal/files", label: "Content & Files", icon: FolderOpen },
  { to: "/portal/calendar", label: "Content Calendar", icon: Calendar },
  { to: "/portal/ads", label: "Paid Ads", icon: Megaphone },
  { to: "/portal/invoices", label: "Invoices", icon: Wallet },
  { to: "/portal/receipts", label: "Receipts", icon: ReceiptIcon },
  { to: "/portal/feedback", label: "Feedback", icon: Heart },
];

export default function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  useDesktopNotifications({ enabled: !!user });
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="portal-layout">
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="flex items-center justify-between px-4 lg:px-8 h-14">
          <Link to="/portal" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-teal text-white flex items-center justify-center font-bold heading">S</div>
            <div className="leading-tight">
              <div className="heading text-base font-semibold tracking-tight">SenrizeFlow</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-teal">Client Portal</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme" data-testid="portal-theme-toggle">
              {theme === "dark" ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
            </Button>
            <div className="hidden sm:flex items-center gap-2">
              <Avatar className="w-8 h-8 ring-2 ring-teal/30">
                <AvatarFallback>{(user?.name || "C").slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="text-xs leading-tight">
                <div className="font-medium">{user?.name}</div>
                <div className="text-muted-foreground capitalize">Client</div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} aria-label="Logout" data-testid="portal-logout">
              <LogOut size={18} strokeWidth={1.5} />
            </Button>
          </div>
        </div>
        <nav className="flex gap-1 px-4 lg:px-8 -mt-1 overflow-x-auto" data-testid="portal-nav">
          {portalNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/portal"}
              data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.15em] border-b-2 ${
                  isActive ? "border-teal text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                }`
              }
            >
              <item.icon size={14} strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="px-4 lg:px-8 py-6 lg:py-8 max-w-[1400px] w-full mx-auto" data-testid="portal-main">
        {children}
      </main>
    </div>
  );
}
